'use client';

/**
 * IntentScheduleCard — one schedule, rendered as a card.
 *
 * The per-item child of the data-driven `IntentSchedulesCard`
 * collection. Cards-everywhere recursion: the explorer surface is a
 * card, the schedules section is a card, and each schedule is a card —
 * generated from the `intent_list` roster, not declared in the matrix.
 *
 * This card earns cardhood (own identity, own selection, own export);
 * its line items do NOT — they have no independent data fetch,
 * affordances, or capture-worth, so they render as a leaf TREE inside
 * this card and ride along in this card's export `json.lines`. Recurse
 * to cards as far as independent units go, then render leaves inline.
 *
 * Thin selector by design: click → `onSelect` (the parent selects the
 * Intent in graph-selection → the rail's brief/lines/match-quality
 * cards paint). Data orchestration (the intent_grid shortlist build,
 * TTL cache, refresh) lives in the parent collection card today, and
 * moves to the canvas in the grid-orchestration cut — it does NOT
 * belong on this card.
 *
 * Export: registers via `useCardExport` keyed by `intent_sid` (the
 * registry keys by `useId`, so N instances each register cleanly).
 * `surface` + per-instance `slot_id` are injected by the
 * `CardPlacementProvider` the parent wraps each child in. `query_key`
 * is the shared roster query (`intent_list`) — every schedule card
 * declares it, so the capture envelope dedups to one roster payload
 * backing N scene entries.
 *
 * @see ./schedules-card.tsx — the collection card that generates these
 * @see klyde_ipe/lib/design/card-export.tsx — the recursion doctrine
 *
 * @component_role: Card
 * @component_type: Explorer Card
 * @domain: Explorer / Intent
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, ClipboardCopy, Download, Loader2,
  MoreVertical, RefreshCw,
} from 'lucide-react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { text, treeDepth, surfaceBase } from '@/lib/design/tokens.x';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import {
  downloadCapture,
  useCardExport,
  waitForRailSelection,
} from '@/lib/design/card-export';
import type { IntentLineRow, IntentSummary } from '@/lib/api/types';

interface IntentScheduleCardProps {
  item: IntentSummary;
  isActive: boolean;
  isOpen: boolean;
  isRefreshing: boolean;
  /** Select this schedule (parent propagates to graph-selection so the
   *  rail paints) + toggle expansion. */
  onSelect: () => void;
  /** Provided only when this card is the active one; absent otherwise. */
  onRefresh?: () => void;
  /** Lines for this schedule, fetched + cached by the parent. undefined
   *  = not yet loaded (expand in progress). */
  lineRows: IntentLineRow[] | undefined;
  selectedLineSid: string | null;
  onLineClick: (lineSid: string) => void;
  /** The roster query that backs every schedule card's content. Shared
   *  across all instances; the capture envelope dedups to one payload. */
  rosterQueryKey: QueryKey | null;
  /** Mode key for the capture envelope (the active explorer view). */
  mode?: string;
  /** Kebab menu labels — passed from the collection card's intent
   *  explorer labels dict (chrome ships from the BFF, not invented here). */
  menuLabel?: string;
  copyLabel?: string;
  downloadCaptureLabel?: string;
}

export function IntentScheduleCard({
  item,
  isActive,
  isOpen,
  isRefreshing,
  onSelect,
  onRefresh,
  lineRows,
  selectedLineSid,
  onLineClick,
  rosterQueryKey,
  mode,
  menuLabel,
  copyLabel,
  downloadCaptureLabel,
}: IntentScheduleCardProps) {
  const queryClient = useQueryClient();
  const lang = useLanguageStore((s) => s.lang);
  const [menuOpen, setMenuOpen] = useState(false);

  // Role-gated menu actions — the matrix ships only the affordances this
  // actor's role may see (visible_to). undefined = not in this actor's
  // projection = don't render. UI hiding is a courtesy; capture_scene
  // enforces independently at the auth boundary.
  const copyTestId = useAffordance('explorer.intent_schedule.copy_report');
  const downloadTestId = useAffordance('explorer.intent_schedule.download_capture');
  // Dynamic — every roster row renders simultaneously, so the select
  // button is addressed by this row's own semantic_id, not a static
  // key. Same reasoning as explorer.project.open / explorer.mcp.open.
  const selectTestId = useAffordance('explorer.intent_schedule.select', { id: item.intent_sid });

  const cohPct = item.coherence != null ? Math.round(item.coherence * 100) : null;
  const metaLine = [
    `${item.line_count} lines`,
    item.aggregate_enrichment_status || null,
    cohPct != null ? `coh ${cohPct}%` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Scene export — this schedule as a captured card. Lines ride in
  // json.lines (leaf presentation). status is 'loaded': the card paints
  // from roster data already in hand. surface + slot_id arrive from the
  // CardPlacementProvider the parent wraps this in. Hoisted to a const so
  // the kebab's Copy can reuse the exact markdown this card registered.
  const exportValue = useMemo(() => {
    const lines = lineRows ?? [];
    const lineText = lines.map((l) => `  • ${l.name || l.line_sid}`).join('\n');
    return {
      label: item.name || item.intent_sid,
      content_key: 'intent_schedule',
      status: 'loaded' as const,
      query_key: rosterQueryKey,
      text: [`${item.name || item.intent_sid} (${metaLine})`, lineText]
        .filter(Boolean)
        .join('\n'),
      markdown: [
        `### ${item.name || item.intent_sid}`,
        metaLine,
        ...lines.map((l) => `- ${l.name || l.line_sid}`),
      ]
        .filter(Boolean)
        .join('\n'),
      json: {
        intent_sid: item.intent_sid,            // identity
        name: item.name ?? '',
        meta: metaLine,                         // painted, as the card renders it
        lines: lines.map((l) => ({
          line_sid: l.line_sid,                 // identity (correlates scene↔payload)
          name: l.name ?? '',
          target_price:
            l.target_price != null ? `$${Number(l.target_price).toFixed(0)}` : null,
        })),
      },
      state: { active: isActive, open: isOpen },
    };
  }, [item, isActive, isOpen, lineRows, cohPct, metaLine, rosterQueryKey]);
  useCardExport(exportValue);

  // Copy this schedule (not the whole surface) — reuse the registered
  // markdown so clipboard == export by construction.
  const handleCopy = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(exportValue.markdown || exportValue.text);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IntentScheduleCard] copy failed:', err);
    }
  };

  // Select-then-capture: if this schedule isn't already the active
  // selection, select it (paints the rail) and wait for the rail trio to
  // load before capturing — the rail is the surface that paints in mode3,
  // so we wait on it, not on a canvas preset swap. Strategy-only download
  // (no fallback); the error is diagnostic.
  const handleDownloadCapture = async () => {
    setMenuOpen(false);
    if (!isActive) {
      onSelect();
      await waitForRailSelection(item.intent_sid, 5000);
    }
    try {
      await downloadCapture(queryClient, {
        mode: mode ?? null,
        selection: item.intent_sid,
        resolved_selection: item.intent_sid,
        selection_source: 'explicit',
        viewer_lang: lang ?? 'en',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IntentScheduleCard] capture failed:', err);
    }
  };

  return (
    <div
      style={{ fontSize: surfaceBase.canvas }}
      className={cn(
        'relative rounded-lg border bg-card transition-colors',
        isActive ? 'border-primary/30 bg-primary/5' : 'border-border hover:bg-accent/30',
      )}
    >
      {/* Header row — selectable; click selects + toggles the line tree */}
      <div className="group flex items-center gap-2 px-2.5 py-2">
        <button
          type="button"
          data-testid={selectTestId}
          onClick={onSelect}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          {isOpen ? (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className={cn(treeDepth[0], 'truncate', isActive && 'text-primary')}>
              {item.name || item.intent_sid}
            </p>
            <p className={cn(text.muted, 'truncate')}>{metaLine}</p>
          </div>
        </button>
        {onRefresh && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRefresh();
            }}
            disabled={isRefreshing}
            title="Refresh — rebuild rail + grid for this schedule"
            className={cn(
              'p-1.5 rounded shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted',
              'transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
          </button>
        )}
        {/* Kebab — per-schedule actions. Present only when this actor's
            role projection carries at least one action (capture is
            admin-only; copy is unrestricted). */}
        {(copyTestId || downloadTestId) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={menuLabel ?? ''}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="p-1.5 rounded shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {menuOpen && (copyTestId || downloadTestId) && (
        <>
          {/* Click-outside backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-10 z-50 w-52 rounded-md border border-border bg-popover shadow-md py-1"
          >
            {copyTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={copyTestId}
                onClick={handleCopy}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left"
              >
                <ClipboardCopy className="w-3.5 h-3.5" />
                {copyLabel ?? ''}
              </button>
            )}
            {downloadTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={downloadTestId}
                onClick={() => void handleDownloadCapture()}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left"
              >
                <Download className="w-3.5 h-3.5" />
                {downloadCaptureLabel ?? ''}
              </button>
            )}
          </div>
        </>
      )}

      {/* Line tree — leaf presentation of this schedule's children. Not
          cards: no independent identity/affordances; they ride in this
          card's export json.lines. */}
      {isOpen && (
        <ul className="px-2 pb-2 space-y-0.5">
          {lineRows === undefined ? (
            <li className={cn(treeDepth[1], 'pl-8 pr-2 py-1 flex items-center gap-2 text-muted-foreground')}>
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading lines…
            </li>
          ) : lineRows.length === 0 ? (
            <li className={cn(treeDepth[1], 'pl-8 pr-2 py-1 text-muted-foreground')}>No lines.</li>
          ) : (
            lineRows.map((ln) => (
              <IntentScheduleLineRow
                key={ln.line_sid}
                line={ln}
                isSelected={selectedLineSid === ln.line_sid}
                onLineClick={onLineClick}
              />
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/** One LineItem row inside an opened schedule — extracted so
 *  `useAffordance` (per-line dynamic address) can be called cleanly
 *  once per rendered row, same pattern as `ComposerLineRow`. Not a
 *  card: no independent export/data-fetch, purely presentational. */
function IntentScheduleLineRow({
  line,
  isSelected,
  onLineClick,
}: {
  line: IntentLineRow;
  isSelected: boolean;
  onLineClick: (lineSid: string) => void;
}) {
  const lineTestId = useAffordance('explorer.intent_schedule.select_line', { line_id: line.line_sid });
  return (
    <li>
      <button
        type="button"
        data-testid={lineTestId}
        onClick={() => onLineClick(line.line_sid)}
        className={cn(
          treeDepth[1],
          'w-full flex items-center gap-2 pl-8 pr-2 py-1 text-left rounded-md',
          'hover:bg-accent/30 transition-colors',
          isSelected && 'bg-primary/10 text-primary',
        )}
        title={line.name}
      >
        <span className="flex-1 min-w-0 truncate">{line.name || line.line_sid}</span>
        {line.target_price != null && (
          <span className={treeDepth[2]}>
            ${Number(line.target_price).toFixed(0)}
          </span>
        )}
      </button>
    </li>
  );
}
